/**
 * Step 5: Checklist de Documentação e Conclusão
 */
import { useState } from "react";
import type { StepWithScoreProps } from "./types.js";
import { Checkbox, Badge } from "./FormElements.jsx";
import { RADIUS } from "../theme.js";
import { subscribeToPush, isPushSupported } from "../../services/pushService.js";

export function Step5Conclusao({ form, onChange, T, score, scoreColor }: StepWithScoreProps) {
  const set = (key: string, value: unknown) => onChange(key, value);
  const [subscribingPush, setSubscribingPush] = useState(false);

  const handlePushToggle = async (enabled: boolean) => {
    if (enabled && isPushSupported()) {
      setSubscribingPush(true);
      const ok = await subscribeToPush();
      setSubscribingPush(false);
      if (ok) {
        set("pushNotificationsEnabled", true);
      }
    } else {
      set("pushNotificationsEnabled", enabled);
    }
  };

  return (
    <>
      <h3 style={{ fontSize: 15, fontWeight: 700, color: T.text, margin: "0 0 8px" }}>
        6. Checklist de Documentação e Conclusão
      </h3>
      <div style={{ marginBottom: 20 }}>
        <Checkbox
          label="📄 DIRPF (Declaração de Imposto de Renda) do último exercício"
          checked={form.chkDIRPF as boolean | undefined}
          onChange={(e) => set("chkDIRPF", e.target.checked)}
          T={T}
        />
        <Checkbox
          label="🏦 Extratos bancários/corretora consolidados do último mês"
          checked={form.chkExtratos as boolean | undefined}
          onChange={(e) => set("chkExtratos", e.target.checked)}
          T={T}
        />
        <Checkbox
          label="📋 Contrato Social / Estatuto (se PJ ou Holding)"
          checked={form.chkContratoSocial as boolean | undefined}
          onChange={(e) => set("chkContratoSocial", e.target.checked)}
          T={T}
        />
        <Checkbox
          label="🏠 Comprovante de Residência atualizado"
          checked={form.chkComprovanteRes as boolean | undefined}
          onChange={(e) => set("chkComprovanteRes", e.target.checked)}
          T={T}
        />
      </div>

      {isPushSupported() && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 8 }}>
            Notificações Push
          </div>
          <Checkbox
            label="Desejo receber notificações push sobre meus investimentos"
            checked={
              (form as Record<string, unknown>).pushNotificationsEnabled as boolean | undefined
            }
            onChange={(e) => handlePushToggle(e.target.checked)}
            T={T}
            disabled={subscribingPush}
          />
          <div style={{ fontSize: 11, color: T.sub, marginLeft: 28, marginTop: 4 }}>
            Relatórios mensais, alertas de drift e lembretes de reunião
          </div>
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 8 }}>
          Assinaturas e Consentimento (LGPD)
        </div>
        <Checkbox
          label="✅ Cliente declara ter ciência e consentimento com a LGPD e o tratamento de seus dados pessoais e patrimoniais."
          checked={form.assinaturaCliente as boolean | undefined}
          onChange={(e) => set("assinaturaCliente", e.target.checked)}
          T={T}
        />
        <Checkbox
          label="✅ Assessor LPA confirma validação do perfil de suitability e dados cadastrais."
          checked={form.assinaturaConsultor as boolean | undefined}
          onChange={(e) => set("assinaturaConsultor", e.target.checked)}
          T={T}
        />
      </div>

      {/* Result summary */}
      <div
        style={{
          background: `${scoreColor}12`,
          border: `2px solid ${scoreColor}40`,
          borderRadius: RADIUS.md,
          padding: "16px 20px",
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 10 }}>
          📊 Resultado Final – Suitability & Status Onboarding
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <div>
            <div style={{ fontSize: 24, fontWeight: 800, color: scoreColor }}>{score.perfil}</div>
            <div style={{ fontSize: 12, color: T.sub }}>
              {score.score}/{score.max} pontos · Resolução CVM 30
            </div>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              alignItems: "flex-end",
            }}
          >
            <Badge
              text={(() => {
                const total = [
                  form.chkDIRPF,
                  form.chkExtratos,
                  form.chkContratoSocial,
                  form.chkComprovanteRes,
                ].filter(Boolean).length;
                return total === 4 ? "✓ Docs completos" : `⏳ Docs ${total}/4`;
              })()}
              color={
                [
                  form.chkDIRPF,
                  form.chkExtratos,
                  form.chkContratoSocial,
                  form.chkComprovanteRes,
                ].every(Boolean)
                  ? "#16A34A"
                  : "#D97706"
              }
            />
            <Badge
              text={
                form.assinaturaCliente && form.assinaturaConsultor
                  ? "✓ Assinado"
                  : "⏳ Pendente Assinatura"
              }
              color={form.assinaturaCliente && form.assinaturaConsultor ? "#16A34A" : "#D97706"}
            />
          </div>
        </div>
      </div>
    </>
  );
}
